import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, OnDestroy, OnInit, AfterViewInit, QueryList, Renderer2, ViewChild, ViewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { AutoCompleteModule, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { Subject, first, fromEvent, debounceTime, takeUntil } from 'rxjs';
import { AuthService } from '../core/auth/auth.service';
import { Profile } from '../core/auth/profile';
import { PROFILE_ROLE } from '../core/auth/profile-role';
import { ProfileService } from '../core/auth/profile.service';
import { Context } from '../core/auth/context';
import { HOSTNAME } from '../core/domain/hostname';
import { WindowRef } from '../core/domain/window-ref';
import { I18NService } from '../core/i18n/i18n.service';
import { Application } from './application';
import { AUTOCOMPLETE_MAX_LIMIT } from './autocomplete-limit';
import { NavbarService } from './navbar.service';
import { DropdownModule } from 'primeng/dropdown';
import { ContextGroup } from './context-group';
import { DomainService } from '../core/domain/domain.service';
import '@visma-swno/gaia-chat-ui';
import { GAIA_BU_NAMES } from './gaia-ai-bu-names';
import { LatestExportComponent } from '../exports/latest-export.component';
import { LatestExportsService } from '../exports/latest-exports.service';


@Component({
  selector: 'adminui-navbar',
  standalone: true,
  imports: [CommonModule, FormsModule, AutoCompleteModule, RouterLink, RouterLinkActive, DropdownModule, LatestExportComponent],
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class NavbarComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('appsToggle') appsToggle!: ElementRef;
  @ViewChild('contextsToggle') contextsToggle!: ElementRef;
  @ViewChild('contextsMenu') contextsMenu!: ElementRef;
  @ViewChild('navbarDefaultHeader') navbarDefaultHeader!: ElementRef;
  @ViewChild('navbarBrandDiv') navbarBrandDiv!: ElementRef;
  @ViewChild('navbarBrandLi') navbarBrandLi!: ElementRef;
  @ViewChild('contextSelectorUl') contextSelectorUl!: ElementRef;
  @ViewChild('userDropDownLi') userDropDownLi!: ElementRef;
  @ViewChild('menuDropLi') menuDropLi!: ElementRef;
  @ViewChild('menuDropSpan') menuDropSpan!: ElementRef;
  @ViewChildren('menuItem') menuItems!: QueryList<ElementRef<HTMLLIElement>>;
  @ViewChildren('droppedMenuItem') droppedMenuItems!: QueryList<ElementRef<HTMLLIElement>>;
  @ViewChild('gaiaChat') gaiaChat!: ElementRef;
  @ViewChild('gaiaChatIcon') gaiaChatIcon!: ElementRef;
  @ViewChild('gaiaDrawer') gaiaDrawer!: ElementRef;

  message: { [key: string]: string; } = {};

  appName = 'Admin';
  apps: Application[] = [];
  contextGroups: ContextGroup[] = [];
  profileRole = PROFILE_ROLE;

  isAppsDropdownOpen = false;
  isContextsDropdownOpen = false;
  isMenuDropOpen = false;
  isDroppedAppsMenuOpen = false;
  isLatestExportsPanelOpen = false;
  contextSelectorWidth: number = 0;
  navbarBrandInitialWidth: number = 0;
  menuItemsInitialWidth: number = 0;
  showHelp: boolean = false;

  appsAriaLabel: string = '';
  infoAriaLabel: string = '';

  pictureStyle: string = '';
  profile!: Profile;

  private requestTokenHandler!: (event: Event) => void;
  private closeRequestedHandler!: (event: Event) => void;
  private authFailedHandler!: (event: Event) => void;

  hostname: string = HOSTNAME.localhost;
  selectedContext!: Context;
  rootContextPath: string = '/';

  private readonly translationIndex = 'navbar';
  private readonly translationIndexHelp = 'help';
  private readonly translationIndexCommon = 'common';

  private destroy$ = new Subject<void>();

  //gaia-chat-ui
  profileId: string = '';
  baseUrl: string = '';
  teamId: string = '';
  lang: string = '';
  isGaiaChatOpen = false;
  showGaiaChatIcon = false;
  
  constructor(private profileService: ProfileService, private i18nService: I18NService,
    private authService: AuthService, private service: NavbarService,
    private renderer2: Renderer2, private windowRef: WindowRef, private elementRef: ElementRef,
    private domainService: DomainService, private latestExportsService: LatestExportsService) { }

  ngOnInit() {

    this.requestTokenHandler = this.handleTokenRequest.bind(this);
    this.closeRequestedHandler = () => {
      this.isGaiaChatOpen = false;
    };
    this.authFailedHandler = (event: Event) => {
      console.error('Auth failed', event);
    };

    this.elementRef.nativeElement.addEventListener('request-token', this.requestTokenHandler);
    this.elementRef.nativeElement.addEventListener('close-requested', this.closeRequestedHandler);
    this.hostname = this.windowRef.getHostname();

    this.profile = this.profileService.profile;
    const businessUnitName = this.profile.currentContext.businessUnitName;
    this.showGaiaChatIcon = GAIA_BU_NAMES.includes(businessUnitName);

    if (this.showGaiaChatIcon) {
      this.profileId = 'admin-prod';
      this.baseUrl = this.domainService.getGaiaChatApiUrl(this.hostname);
      this.lang = this.profile.locale.split('-')[0];

      const interval = setInterval(() => {
        if (this.gaiaChat && this.gaiaChat.nativeElement) {
          clearInterval(interval);
          this.gaiaChat.nativeElement.authStrategy = 'visma';
        }
      }, 0);
    }
    
    this.rootContextPath = this.service.getRootContextPath(this.profile.roles);
    this.selectedContext = structuredClone(this.profile.currentContext);
    this.message = {
      ...this.i18nService.messages[this.translationIndex], ...this.i18nService.messages[this.translationIndexHelp],
      ...this.i18nService.messages[this.translationIndexCommon]
    };
    this.appName = this.service.getAppName(this.profile.roles);

    this.appsAriaLabel = this.i18nService.getFormattedMessage(this.message['applicationsAriaLabel'],
      [this.appName]);
    this.pictureStyle = `background-image: url(${this.profile.picture});`;
    this.infoAriaLabel = this.i18nService.getFormattedMessage(this.message['informationAriaLabel'],
      [this.profile.currentContext.name]);

    const page = 1;
    const limit = AUTOCOMPLETE_MAX_LIMIT;

    this.authService.getContextResponse(this.hostname, page, limit).pipe(first()).subscribe(data => {
      this.contextGroups = this.service.getContextGroups(data.contexts, this.message);

      this.service.getApplications().pipe(first()).subscribe(response => {
        this.apps = response;
        this.navbarBrandInitialWidth = this.navbarBrandDiv.nativeElement.offsetWidth;
        this.menuItemsInitialWidth = this.service.getMenuItemsTotalWidth(this.menuItems.toArray());
        this.contextSelectorWidth = this.contextSelectorUl.nativeElement.offsetWidth;

        this.handleResponsiveness(this.navbarBrandInitialWidth, this.menuItemsInitialWidth,
          this.contextSelectorWidth);
      });

    });

    this.openLatestExportsPanel();
    this.documentClickListener();
    this.keyupListener();
    this.resizeListener();
  }

  ngAfterViewInit() {
    if (this.showGaiaChatIcon) {
      this.gaiaChat.nativeElement.tokenProvider = () => this.authService.getAccessToken();
      this.gaiaChat.nativeElement.addEventListener('auth-failed', this.authFailedHandler);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.elementRef.nativeElement.removeEventListener('request-token', this.requestTokenHandler!);
    this.elementRef.nativeElement.removeEventListener('close-requested', this.closeRequestedHandler!);
    this.elementRef.nativeElement.removeEventListener('auth-failed', this.authFailedHandler!)
  }

  toggleAppsDropdown(event: Event) {
    event.preventDefault();
    this.isAppsDropdownOpen = !this.isAppsDropdownOpen;
    if (this.isAppsDropdownOpen) {
      this.isContextsDropdownOpen = false;
      this.isMenuDropOpen = false;
      this.isGaiaChatOpen = false;
    }
  }

  toggleMenuDrop(event: Event) {
    event.preventDefault();
    this.isMenuDropOpen = !this.isMenuDropOpen;
    if (this.isMenuDropOpen) {
      this.isAppsDropdownOpen = false;
      this.isContextsDropdownOpen = false;
      this.isGaiaChatOpen = false;
    }
  }

  toggleDroppedAppsMenu(event: Event) {
    event.preventDefault();
    this.isDroppedAppsMenuOpen = !this.isDroppedAppsMenuOpen;
  }

  toggleContextsDropdown(event: Event) {
    event.preventDefault();
    this.isContextsDropdownOpen = !this.isContextsDropdownOpen;
    if (this.isContextsDropdownOpen) {
      this.isAppsDropdownOpen = false;
      this.isMenuDropOpen = false;
      this.isGaiaChatOpen = false;
    }
  }

  onSearchContext(event: any): void {
    const page = 1;
    const limit = AUTOCOMPLETE_MAX_LIMIT;
    if (event.originalEvent.type === 'click') {
      this.authService.getContextResponse(this.hostname, page, limit).pipe(first()).subscribe(data => {
        this.contextGroups = this.service.getContextGroups(data.contexts, this.message);
      });
    } else {
      this.authService.getContextResponse(this.hostname, page, limit, event.query).pipe(first()).subscribe(data => {
        if (data === null) {
          this.contextGroups = [];
        } else {
          this.contextGroups = this.service.getContextGroups(data.contexts, this.message);
        }
      });
    }
  }

  onSelectContext(event: AutoCompleteSelectEvent): void {
    const context: Context = event.value;
    if (this.profileService.profile.currentContext.contextId !== context.contextId) {
      this.profileService.profile.currentContext = context;
      this.authService.patchCustomWithLastUsedContext(context.contextId, context.name).pipe(first()).subscribe(() => {
        this.authService.setCurrentContextOnStorage(context);
        const url = this.authService.getConnectLoginUrl(this.hostname);
        this.windowRef.setLocationHref(url);
      });
    }
  }

  onFocus(event: Event): void {
    (event.currentTarget as HTMLInputElement).select();
  }

  onBlurContext(): void {
    this.selectedContext = structuredClone(this.profile.currentContext);
  }

  onClickHelp(event: Event): void {
    event.preventDefault();
    let url = "https://docs.vismasoftware.no/admin/";
    switch (this.profileService.profile.locale) {
      case 'nb-NO': url = "https://docs.vismasoftware.no/no/admin/";
        break;
      case 'sv-SE': url = "https://docs.vismasoftware.no/sv/admin/";
        break;
    }
    this.windowRef.open(url, '_blank');
  }

  onClickGaiaChat(event: Event): void {
    event.preventDefault();
    this.isGaiaChatOpen = !this.isGaiaChatOpen;
    this.isContextsDropdownOpen = false;
    this.isMenuDropOpen = false;
    this.isAppsDropdownOpen = false;
    this.isLatestExportsPanelOpen = false;
  }

  onClickLatestExports(event: Event) {
    event.preventDefault();
    this.isGaiaChatOpen = false;
    this.isContextsDropdownOpen = false;
    this.isMenuDropOpen = false;
    this.isAppsDropdownOpen = false;
    this.isLatestExportsPanelOpen = !this.isLatestExportsPanelOpen;
    if (this.isLatestExportsPanelOpen) {
      this.latestExportsService.loadLatestExports();
    }
  }

  private handleTokenRequest(event: Event) {
    // Access the callback function passed via the event detail
    const customEvent = event as CustomEvent;
    const { callback } = customEvent.detail;

    const tokenStr = sessionStorage.getItem('tokenResponse');
    if (tokenStr) {
      const token = JSON.parse(tokenStr);
      // Define how to obtain or generate the access token
      const accessToken = token.access_token;
      // Use the callback to set the token in the Lit component
      callback(accessToken);
    }
  }

  private openLatestExportsPanel(): void {
    this.latestExportsService.openLatestExportsPanel$
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value) {
          this.latestExportsService.loadLatestExports();
          this.isGaiaChatOpen = false;
          this.isContextsDropdownOpen = false;
          this.isMenuDropOpen = false;
          this.isAppsDropdownOpen = false;
          this.isLatestExportsPanelOpen = true;
        } else {
          this.isLatestExportsPanelOpen = false;
        }
      });
  }

  private resizeListener(): void {
    fromEvent(window, 'resize')
      .pipe(debounceTime(50), takeUntil(this.destroy$))
      .subscribe(() => {
        this.handleResponsiveness(this.navbarBrandInitialWidth, this.menuItemsInitialWidth,
          this.contextSelectorWidth);
      });
  }

  private documentClickListener(): void {
    fromEvent(document, 'click').pipe(takeUntil(this.destroy$)).subscribe((event: Event) => {
      this.closeAppsDropdown(event);
      this.closeContextsDropdown(event);
    });
  }

  private closeAppsDropdown(event: Event): void {
    if (this.isAppsDropdownOpen) {
      if (event.target !== this.appsToggle.nativeElement && !this.appsToggle.nativeElement.contains(event.target)) {
        this.isAppsDropdownOpen = false;
      }
    }
  }

  private closeContextsDropdown(event: Event): void {
    if (this.isContextsDropdownOpen) {
      if (event.target !== this.contextsToggle.nativeElement && event.target !== this.contextsMenu.nativeElement &&
        !this.contextsToggle.nativeElement.contains(event.target) && !this.contextsMenu.nativeElement.contains(event.target)) {
        this.isContextsDropdownOpen = false;
      }
    }
  }

  private keyupListener(): void {
    fromEvent(document, 'keyup').pipe(takeUntil(this.destroy$)).subscribe((event: Event) => {
      const e = (event as KeyboardEvent);
      const c = e.key;
      if (e.altKey) {
        if (this.isExpectedKey(c, 'W')) {
          this.showHelp = !this.showHelp;
        }
        if (this.isExpectedKey(c, 'P')) {
          this.toggleAppsDropdown(event);
        }
        if (this.isExpectedKey(c, 'K')) {
          this.toggleContextsDropdown(event);
        }
        if (this.isExpectedKey(c, 'U')) {
          this.windowRef.open('https://docs.google.com/forms/d/e/1FAIpQLSeL252hiiMNwQfQy4-NH9sO5bIrTBCRWacdrXRPfaH5UnYT8g/viewform', '_blank');
        }
      }
    });
  }

  private isExpectedKey(c: string, key: string): boolean {
    let is = false;
    if (c === key || c === key.toLowerCase()) {
      is = true;
    }
    return is;
  }

  private handleResponsiveness(navbarBrandWidth: number, menuItemsWidth: number, contextSelectorWidth: number): void {
    const userDropdownEl = this.userDropDownLi.nativeElement;

    if (this.service.isMobile()) {
      this.handleMobileSize(this.navbarDefaultHeader.nativeElement, this.navbarBrandDiv.nativeElement,
        this.menuDropSpan.nativeElement, this.navbarBrandLi.nativeElement);
      this.renderer2.addClass(this.userDropDownLi.nativeElement, 'icon');
      this.renderer2.removeClass(this.menuDropLi.nativeElement, 'hidden');
      this.hideAllMenuItems();
      return;
    }

    if (this.service.hasAllElementsDisplayed(navbarBrandWidth, menuItemsWidth, contextSelectorWidth)) {
      this.handleScreenSize(this.navbarDefaultHeader.nativeElement, this.navbarBrandDiv.nativeElement,
        this.menuDropSpan.nativeElement, this.navbarBrandLi.nativeElement);
      this.renderer2.removeClass(this.userDropDownLi.nativeElement, 'icon');
      this.renderer2.addClass(this.menuDropLi.nativeElement, 'hidden');
      this.showAllMenuItems();
      return;
    }

    this.renderer2.addClass(userDropdownEl, 'icon');
    this.handleElements();
  }

  private handleElements() {
    const navbarDefaultHeaderEl = this.navbarDefaultHeader.nativeElement;
    const navbarBrandDivEl = this.navbarBrandDiv.nativeElement;
    const contextSelectorUlEl = this.contextSelectorUl.nativeElement;
    const menuDropLiEl = this.menuDropLi.nativeElement;
    const menuDropSpanEl = this.menuDropSpan.nativeElement;
    const navbarBrandLiEl = this.navbarBrandLi.nativeElement;
    const arrMenuItems = this.menuItems.toArray();
    const arrDroppedMenuItems = this.droppedMenuItems.toArray();

    this.handleScreenSize(navbarDefaultHeaderEl, navbarBrandDivEl, menuDropSpanEl, navbarBrandLiEl);
    this.refreshMenuItems(arrMenuItems, arrDroppedMenuItems);
    this.renderer2.removeClass(menuDropLiEl, 'hidden');

    let width = document.body.clientWidth - navbarBrandDivEl.offsetWidth - contextSelectorUlEl.offsetWidth - menuDropLiEl.offsetWidth;
    this.handleMenuItems(arrMenuItems, arrDroppedMenuItems, width);

    if (this.service.hasDroppedMenuItems(arrMenuItems)) {
      this.renderer2.removeClass(menuDropLiEl, 'hidden');
    } else {
      this.renderer2.addClass(menuDropLiEl, 'hidden');
    }

    if (this.service.hasDroppedAllMenuItems(arrMenuItems)) {
      this.handleMobileSize(navbarDefaultHeaderEl, navbarBrandDivEl, menuDropSpanEl, navbarBrandLiEl);
    } else {
      this.handleScreenSize(navbarDefaultHeaderEl, navbarBrandDivEl, menuDropSpanEl, navbarBrandLiEl);
    }
  }

  private hideAllMenuItems(): void {
    const arrMenuItems = this.menuItems.toArray();
    const arrDroppedMenuItems = this.droppedMenuItems.toArray();
    for (let i = 0, len = arrMenuItems.length; i < len; i++) {
      this.renderer2.removeClass(arrDroppedMenuItems[i].nativeElement, 'hidden');
      this.renderer2.addClass(arrMenuItems[i].nativeElement, 'hidden');
    }
  }

  private showAllMenuItems(): void {
    const arrMenuItems = this.menuItems.toArray();
    const arrDroppedMenuItems = this.droppedMenuItems.toArray();
    for (let i = 0, len = arrMenuItems.length; i < len; i++) {
      this.renderer2.addClass(arrDroppedMenuItems[i].nativeElement, 'hidden');
      this.renderer2.removeClass(arrMenuItems[i].nativeElement, 'hidden');
    }
  }

  handleMenuItems(arrMenuItems: ElementRef<HTMLLIElement>[], arrDroppedMenuItems: ElementRef<HTMLLIElement>[], width: number): void {
    const activeIndex = this.service.getActivelMenuItemIndex(arrMenuItems);
    if (activeIndex !== -1) {
      const cw = arrMenuItems[activeIndex].nativeElement.offsetWidth;
      width -= cw;
      this.handleMenuItem(arrMenuItems, arrDroppedMenuItems, activeIndex, width);

      for (let i = 0; i < activeIndex; i++) {
        const cw = arrMenuItems[i].nativeElement.offsetWidth;
        width -= cw;
        this.handleMenuItem(arrMenuItems, arrDroppedMenuItems, i, width);
      }

      for (let len = arrMenuItems.length, i = len - 1; i > activeIndex; i--) {
        const cw = arrMenuItems[i].nativeElement.offsetWidth;
        width -= cw;
        this.handleMenuItem(arrMenuItems, arrDroppedMenuItems, i, width);
      }
    }
  }

  private handleMenuItem(arrMenuItems: ElementRef<HTMLLIElement>[], arrDroppedMenuItems: ElementRef<HTMLLIElement>[], index: number, width: number): void {
    if (width < 0) {
      this.renderer2.removeClass(arrDroppedMenuItems[index].nativeElement, 'hidden');
      this.renderer2.addClass(arrMenuItems[index].nativeElement, 'hidden');
    } else {
      this.renderer2.addClass(arrDroppedMenuItems[index].nativeElement, 'hidden');
      this.renderer2.removeClass(arrMenuItems[index].nativeElement, 'hidden');
    }
  }

  private handleMobileSize(navbarDefaultHeaderEl: HTMLElement, navbarBrandDivEl: HTMLDivElement,
    menuDropSpanEl: HTMLSpanElement, navbarBrandLiEl: HTMLLIElement): void {
    this.renderer2.addClass(navbarDefaultHeaderEl, 'is-mobile');
    this.renderer2.addClass(navbarBrandDivEl, 'hidden');
    this.renderer2.removeClass(menuDropSpanEl, 'hidden');
    this.renderer2.removeClass(navbarBrandLiEl, 'hidden');
  }

  private handleScreenSize(navbarDefaultHeaderEl: HTMLElement, navbarBrandDivEl: HTMLDivElement,
    menuDropSpanEl: HTMLSpanElement, navbarBrandLiEl: HTMLLIElement): void {
    this.renderer2.removeClass(navbarDefaultHeaderEl, 'is-mobile');
    this.renderer2.removeClass(navbarBrandDivEl, 'hidden');
    this.renderer2.addClass(menuDropSpanEl, 'hidden');
    this.renderer2.addClass(navbarBrandLiEl, 'hidden');
  }

  private refreshMenuItems(arrMenuItems: ElementRef<HTMLLIElement>[], arrDroppedMenuItems: ElementRef<HTMLLIElement>[]): void {
    for (let i = 0, len = arrMenuItems.length; i < len; i++) {
      this.renderer2.addClass(arrDroppedMenuItems[i].nativeElement, 'hidden');
      this.renderer2.removeClass(arrMenuItems[i].nativeElement, 'hidden');
    }
  }

}
